import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { PageHeader, PageContent } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Copy, Edit, ExternalLink, MoreHorizontal, Play, Plus, Radio, RefreshCw, Save, ScrollText, Send, Square, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const emptyEvent = {
  title: "",
  description: "",
  competition: "",
  sport: "Calcio",
  home_team: "",
  away_team: "",
  date: "",
  time: "",
  image: "",
  hls_url: "",
  m3u_url: "",
  backup_url: "",
  telegram_channel_id: "",
  telegram_stream_key: "",
  publication_mode: "web_only",
  library_channel_id: "",
  publish_at: "",
  status: "scheduled",
  active: true,
  require_membership: false,
  token_expiration_minutes: 60,
  max_access_per_user: 3,
};

const emptyChannel = {
  name: "",
  bot_token: "",
  chat_id: "",
  active: true,
};

const emptyTemplate = {
  name: "Template Streaming",
  body: "📺 <b>{title}</b>\n\n🏆 {competition}\n⚽ {home_team} vs {away_team}\n🗓 {date} - {time}\n\n{description}\n\n▶️ Guarda la diretta dal pulsante qui sotto.",
  buttons: [{ text: "▶️ Guarda Streaming", url: "{telegram_deep_link}" }],
  parse_mode: "HTML",
  active: true,
};

const emptyRequiredChannel = {
  nome: "",
  username: "",
  telegram_chat_id: "",
  bot_channel_id: "",
  obbligatorio: true,
  ordine: 0,
  attivo: true,
};

function asButtonsText(buttons) {
  return (buttons || []).map(button => `${button.text || ""}|${button.url || ""}`).join("\n");
}

function parseButtons(value) {
  return String(value || "")
    .split("\n")
    .map(row => row.trim())
    .filter(Boolean)
    .map(row => {
      const [text, ...rest] = row.split("|");
      return { text: text.trim(), url: rest.join("|").trim() };
    })
    .filter(button => button.text && button.url);
}

export default function StreamingPage() {
  const [tab, setTab] = useState("events");
  const [events, setEvents] = useState([]);
  const [channels, setChannels] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [libraryChannels, setLibraryChannels] = useState([]);
  const [playlistName, setPlaylistName] = useState("Playlist M3U");
  const [playlistContent, setPlaylistContent] = useState("");
  const [librarySearch, setLibrarySearch] = useState("");
  const [libraryGroup, setLibraryGroup] = useState("");
  const [libraryGroups, setLibraryGroups] = useState([]);
  const [eventForm, setEventForm] = useState(emptyEvent);
  const [channelForm, setChannelForm] = useState(emptyChannel);
  const [templateForm, setTemplateForm] = useState(emptyTemplate);
  const [buttonsText, setButtonsText] = useState(asButtonsText(emptyTemplate.buttons));
  const [liveLoading, setLiveLoading] = useState({});
  const [liveLogEvent, setLiveLogEvent] = useState(null);
  const [liveLogs, setLiveLogs] = useState([]);
  const [requiredChannels, setRequiredChannels] = useState([]);
  const [requiredChannelForm, setRequiredChannelForm] = useState(emptyRequiredChannel);
  const [accessStats, setAccessStats] = useState(null);
  const [accessBot, setAccessBot] = useState(null);
  const libraryGroupRef = useRef("");
  const librarySearchRef = useRef("");

  const load = useCallback(async (filters = {}) => {
    const selectedGroup = Object.prototype.hasOwnProperty.call(filters, "libraryGroup")
      ? filters.libraryGroup
      : libraryGroupRef.current;
    const selectedSearch = Object.prototype.hasOwnProperty.call(filters, "librarySearch")
      ? filters.librarySearch
      : librarySearchRef.current;

    const safeGet = async (url, fallback = [], config = {}) => {
      try {
        const { data } = await api.get(url, config);
        return data || fallback;
      } catch (err) {
        console.error(`Errore caricamento ${url}`, err);
        return fallback;
      }
    };

    const [eventsData, channelsData, templatesData, playlistsData, libraryData, groupsData, requiredChannelsData, accessStatsData, accessBotData] = await Promise.all([
      safeGet("/streaming/events"),
      safeGet("/streaming/channels"),
      safeGet("/streaming/templates"),
      safeGet("/streaming/playlists"),
      safeGet("/streaming/library-channels", [], {
        params: {
          q: selectedSearch || undefined,
          group: selectedGroup || undefined,
          limit: 3000,
        },
      }),
      safeGet("/streaming/library-groups"),
      safeGet("/streaming/required-channels"),
      safeGet("/streaming/access-stats", null),
      safeGet("/settings/telegram-access-bot", null),
    ]);

    setEvents(eventsData);
    setChannels(channelsData);
    setTemplates(templatesData);
    setPlaylists(playlistsData);
    setLibraryChannels(libraryData);
    setRequiredChannels(requiredChannelsData);
    setAccessStats(accessStatsData);
    setAccessBot(accessBotData);
    if (typeof setLibraryGroups === "function") setLibraryGroups(groupsData);

    if (templatesData?.[0]) {
      setTemplateForm(templatesData[0]);
      setButtonsText(asButtonsText(templatesData[0].buttons));
    }
  }, []);

  useEffect(() => {
    load().catch(() => toast.error("Errore caricamento Streaming"));
  }, [load]);

  useEffect(() => {
    if (tab !== "events") return undefined;
    const timer = window.setInterval(() => {
      load().catch(() => {});
    }, 5000);
    return () => window.clearInterval(timer);
  }, [load, tab]);

  const channelById = useMemo(() => {
    return Object.fromEntries(channels.map(channel => [channel.id, channel]));
  }, [channels]);

  const accessChart = accessStats?.chart?.series || [];

  const eventDeepLink = (event) => {
    if (event?.telegram_deep_link) return event.telegram_deep_link;
    if (event?.watch_url && String(event.watch_url).includes("t.me/")) return event.watch_url;
    const username = String(event?.access_bot_username || accessBot?.username || "").replace(/^@/, "").trim();
    return username && event?.id ? `https://t.me/${username}?start=live_${event.id}` : "";
  };

  const saveEvent = async () => {
    if (!eventForm.title || !eventForm.telegram_channel_id) {
      toast.error("Titolo e canale Telegram sono obbligatori");
      return;
    }

    if (eventForm.id) {
      await api.patch(`/streaming/events/${eventForm.id}`, eventForm);
      toast.success("Evento aggiornato");
    } else {
      await api.post("/streaming/events", eventForm);
      toast.success("Evento creato");
    }

    setEventForm(emptyEvent);
    load();
  };

  const editEvent = (event) => {
    setEventForm({
      ...emptyEvent,
      ...event,
      publication_mode: event.publication_mode === "web_telegram" ? "web_telegram" : "web_only",
    });
    setTab("events");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Eliminare evento Streaming?")) return;
    await api.delete(`/streaming/events/${id}`);
    toast.success("Evento eliminato");
    load();
  };

  const duplicateEvent = async (id) => {
    await api.post(`/streaming/events/${id}/duplicate`);
    toast.success("Evento duplicato");
    load();
  };

  const publishEvent = async (id) => {
    const { data } = await api.post(`/streaming/events/${id}/publish`);
    toast.success("Pubblicato su Telegram");
    const url = data?.telegram_deep_link || data?.watch_url;
    if (url) window.open(url, "_blank");
    load();
  };

  const copyEventLink = async (event) => {
    const url = eventDeepLink(event);
    if (!url) {
      toast.error("Telegram Access Bot non configurato");
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Deep link Telegram copiato");
    } catch (_error) {
      window.prompt("Copia link evento", url);
    }
  };

  const copyProtectedLink = async (event) => {
    const url = eventDeepLink(event);
    if (!url) {
      toast.error("Telegram Access Bot non configurato");
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link protetto copiato");
    } catch (_error) {
      window.prompt("Copia link protetto", url);
    }
  };

  const liveAction = async (event, action) => {
    setLiveLoading(prev => ({ ...prev, [event.id]: action }));
    try {
      await api.post(`/streaming/events/${event.id}/live/${action}`);
      const labels = { start: "avviata", stop: "fermata", restart: "riavviata" };
      toast.success(`Live ${labels[action] || "aggiornata"}`);
      await load();
      if (liveLogEvent?.id === event.id) await openLiveLogs(event);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Comando Live non riuscito");
    } finally {
      setLiveLoading(prev => ({ ...prev, [event.id]: "" }));
    }
  };

  const testLiveSource = async (event) => {
    setLiveLoading(prev => ({ ...prev, [event.id]: "test-source" }));
    try {
      const { data } = await api.post(`/streaming/events/${event.id}/live/test-source`);
      await load();
      if (data?.ok) {
        toast.success("Sorgente video raggiungibile");
      } else {
        toast.error(`Sorgente non raggiungibile: ${data?.error || "errore sconosciuto"}`);
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Test sorgente non riuscito");
    } finally {
      setLiveLoading(prev => ({ ...prev, [event.id]: "" }));
    }
  };

  const openLiveLogs = async (event) => {
    setLiveLogEvent(event);
    const { data } = await api.get(`/streaming/events/${event.id}/live/logs`);
    const runtimeLines = data?.runtime?.logs || [];
    const historyLines = (data?.history || []).map(row => {
      const when = row.created_at || "";
      const level = row.level || "info";
      return `${when} [${level}] ${row.message || ""}`;
    });
    setLiveLogs([...historyLines, ...runtimeLines].slice(-300));
  };

  const saveChannel = async () => {
    if (!channelForm.name || !channelForm.bot_token || !channelForm.chat_id) {
      toast.error("Nome, Bot Token e Chat ID sono obbligatori");
      return;
    }

    if (channelForm.id) {
      await api.patch(`/streaming/channels/${channelForm.id}`, channelForm);
      toast.success("Canale aggiornato");
    } else {
      await api.post("/streaming/channels", channelForm);
      toast.success("Canale creato");
    }

    setChannelForm(emptyChannel);
    load();
  };

  const saveRequiredChannel = async () => {
    if (!requiredChannelForm.nome && !requiredChannelForm.username) {
      toast.error("Nome o username del canale obbligatorio richiesto");
      return;
    }

    let saved = null;
    if (requiredChannelForm.id) {
      const { data } = await api.patch(`/streaming/required-channels/${requiredChannelForm.id}`, requiredChannelForm);
      saved = data;
      toast.success("Canale obbligatorio aggiornato");
    } else {
      const { data } = await api.post("/streaming/required-channels", requiredChannelForm);
      saved = data;
      toast.success("Canale obbligatorio aggiunto");
    }
    setRequiredChannelForm(emptyRequiredChannel);
    if (saved?.id && saved?.username) {
      await verifyRequiredChannel(saved);
      return;
    }
    load();
  };

  const verifyRequiredChannel = async (channel) => {
    if (!channel?.id) {
      toast.error("Salva prima il canale, poi verifica.");
      return;
    }
    try {
      const { data } = await api.post(`/streaming/required-channels/${channel.id}/verify`);
      if (data?.configured) {
        toast.success("Canale configurato: bot amministratore e Chat ID recuperato");
      } else {
        toast.error(data?.message || "Aggiungi il bot come amministratore del canale e riprova.");
      }
      await load();
      if (data?.channel) setRequiredChannelForm(prev => prev.id === channel.id ? { ...emptyRequiredChannel, ...data.channel } : prev);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Verifica canale non riuscita");
    }
  };

  const deleteRequiredChannel = async (id) => {
    if (!window.confirm("Rimuovere questo canale obbligatorio?")) return;
    await api.delete(`/streaming/required-channels/${id}`);
    toast.success("Canale obbligatorio rimosso");
    load();
  };

  const deleteChannel = async (id) => {
    if (!window.confirm("Eliminare canale Streaming?")) return;
    await api.delete(`/streaming/channels/${id}`);
    toast.success("Canale eliminato");
    load();
  };

  
  const importPlaylist = async () => {
    if (!playlistName || !playlistContent) {
      toast.error("Nome playlist e contenuto M3U sono obbligatori");
      return;
    }

    const { data } = await api.post("/streaming/playlists/import", {
      name: playlistName,
      content: playlistContent,
      inactive_missing: true,
    });

    toast.success(`Playlist importata: ${data.imported} canali`);
    setPlaylistContent("");
    load();
  };

  const loadPlaylistFile = async (file) => {
    if (!file) return;
    const content = await file.text();
    setPlaylistContent(content);
    if (file.name) setPlaylistName(file.name.replace(/\.[^.]+$/, ""));
  };

  const updateLibraryChannel = async (id, patch) => {
    await api.patch(`/streaming/library-channels/${id}`, patch);
    toast.success("Canale aggiornato");
    load();
  };

  const deletePlaylist = async (id) => {
    if (!window.confirm("Eliminare playlist e canali importati?")) return;
    await api.delete(`/streaming/playlists/${id}`);
    toast.success("Playlist eliminata");
    load();
  };

  const changeLibraryGroup = (value) => {
    const nextGroup = value === "__all" ? "" : value;
    libraryGroupRef.current = nextGroup;
    setLibraryGroup(nextGroup);
    load({ libraryGroup: nextGroup });
  };

  const changeLibrarySearch = (value) => {
    librarySearchRef.current = value;
    setLibrarySearch(value);
  };

const saveTemplate = async () => {
    const payload = { ...templateForm, buttons: parseButtons(buttonsText) };

    if (payload.id) {
      await api.patch(`/streaming/templates/${payload.id}`, payload);
      toast.success("Template aggiornato");
    } else {
      await api.post("/streaming/templates", payload);
      toast.success("Template creato");
    }

    load();
  };

  const field = (label, key, props = {}) => (
    <div>
      <Label>{label}</Label>
      <Input
        value={eventForm[key] || ""}
        onChange={e => setEventForm({ ...eventForm, [key]: e.target.value })}
        className="bg-slate-950 border-slate-800 mt-1"
        {...props}
      />
    </div>
  );

  return (
    <div data-testid="streaming-page">
      <PageHeader title="Streaming" subtitle="Gestisci dirette, pagine pubbliche e pubblicazione Telegram dedicata.">
        <div className="flex gap-2">
          {["events", "library", "access", "channels", "templates"].map(item => (
            <Button key={item} variant={tab === item ? "default" : "outline"} onClick={() => setTab(item)}>
              {item === "events" ? "Eventi" : item === "library" ? "Libreria Canali" : item === "access" ? "Accesso Live" : item === "channels" ? "Canali Telegram" : "Template"}
            </Button>
          ))}
        </div>
      </PageHeader>

      <PageContent>
        {tab === "events" && (
          <div className="space-y-6">
            <div className="bg-slate-900 border border-slate-800 rounded-md p-4">
              <div className="flex items-center gap-2 mb-4">
                <Radio size={18} className="text-blue-300" />
                <h2 className="text-white font-semibold">{eventForm.id ? "Modifica evento" : "Nuovo evento Streaming"}</h2>
              </div>

              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                {field("Titolo", "title")}
                {field("Competizione", "competition")}
                {field("Sport", "sport")}
                {field("Squadra casa", "home_team")}
                {field("Squadra ospite", "away_team")}
                {field("Data", "date", { type: "date" })}
                {field("Ora", "time", { type: "time" })}
                {field("Immagine", "image")}
                {field("Link HLS", "hls_url")}
                {field("Link M3U", "m3u_url")}
                {field("Link Backup", "backup_url")}
                <div className="md:col-span-2 xl:col-span-3 rounded-md border border-slate-800 bg-slate-950 p-3">
                  <Label>Modalità di pubblicazione</Label>
                  <RadioGroup
                    value={eventForm.publication_mode || "web_only"}
                    onValueChange={value => setEventForm({ ...eventForm, publication_mode: value })}
                    className="mt-3 gap-3 sm:grid-cols-2"
                  >
                    <label className="flex cursor-pointer items-start gap-3 rounded-md border border-slate-800 p-3">
                      <RadioGroupItem value="web_only" className="mt-0.5" />
                      <span>
                        <span className="block text-sm font-medium text-slate-100">Solo Player Web</span>
                        <span className="mt-1 block text-xs text-slate-500">Avvia soltanto l'anteprima HLS sul player web.</span>
                      </span>
                    </label>
                    <label className="flex cursor-pointer items-start gap-3 rounded-md border border-slate-800 p-3">
                      <RadioGroupItem value="web_telegram" className="mt-0.5" />
                      <span>
                        <span className="block text-sm font-medium text-slate-100">Player Web + Telegram Live</span>
                        <span className="mt-1 block text-xs text-slate-500">Avvia anche l'uscita RTMP verso Telegram.</span>
                      </span>
                    </label>
                  </RadioGroup>
                </div>
                {eventForm.publication_mode === "web_telegram" && field("Telegram Stream Key", "telegram_stream_key", { type: "password", autoComplete: "off" })}
                {field("Pubblica il", "publish_at", { type: "datetime-local" })}

                <div>
                  <Label>Canale Telegram Streaming</Label>
                  <Select value={eventForm.telegram_channel_id || ""} onValueChange={value => setEventForm({ ...eventForm, telegram_channel_id: value })}>
                    <SelectTrigger className="bg-slate-950 border-slate-800 mt-1"><SelectValue placeholder="Seleziona canale" /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                      {channels.map(channel => <SelectItem key={channel.id} value={channel.id}>{channel.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Canale dalla Libreria M3U</Label>
                  <Select value={eventForm.library_channel_id || ""} onValueChange={value => setEventForm({ ...eventForm, library_channel_id: value })}>
                    <SelectTrigger className="bg-slate-950 border-slate-800 mt-1"><SelectValue placeholder="Seleziona stream" /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 max-h-80">
                      {libraryChannels.filter(c => c.active).map(channel => (
                        <SelectItem key={channel.id} value={channel.id}>
                          {channel.name}{channel.group ? ` · ${channel.group}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Stato evento</Label>
                  <Select value={eventForm.status || "scheduled"} onValueChange={value => setEventForm({ ...eventForm, status: value })}>
                    <SelectTrigger className="bg-slate-950 border-slate-800 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                      <SelectItem value="scheduled">Programmato</SelectItem>
                      <SelectItem value="live">Live</SelectItem>
                      <SelectItem value="error">Errore</SelectItem>
                      <SelectItem value="ended">Terminato</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="rounded-md border border-slate-800 bg-slate-950 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <Label>Richiede iscrizione</Label>
                      <div className="text-xs text-slate-500 mt-1">
                        Blocca la pagina live finché l'utente non risulta iscritto ai canali obbligatori.
                      </div>
                    </div>
                    <Switch
                      checked={!!eventForm.require_membership}
                      onCheckedChange={value => setEventForm({ ...eventForm, require_membership: value })}
                    />
                  </div>
                </div>

                <div>
                  <Label>Durata token accesso (minuti)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="1440"
                    value={eventForm.token_expiration_minutes || 60}
                    onChange={e => setEventForm({ ...eventForm, token_expiration_minutes: Number(e.target.value) })}
                    className="bg-slate-950 border-slate-800 mt-1"
                  />
                </div>

                <div>
                  <Label>Accessi massimi per verifica</Label>
                  <Input
                    type="number"
                    min="1"
                    max="1000"
                    value={eventForm.max_access_per_user || 3}
                    onChange={e => setEventForm({ ...eventForm, max_access_per_user: Number(e.target.value) })}
                    className="bg-slate-950 border-slate-800 mt-1"
                  />
                </div>

                <div className="md:col-span-2 xl:col-span-3">
                  <Label>Descrizione</Label>
                  <textarea
                    value={eventForm.description || ""}
                    onChange={e => setEventForm({ ...eventForm, description: e.target.value })}
                    className="w-full min-h-[90px] bg-slate-950 border border-slate-800 rounded-md mt-1 px-3 py-2 text-sm text-slate-100"
                  />
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                <Button onClick={saveEvent} className="bg-blue-600 hover:bg-blue-700"><Save size={16} className="mr-2" />Salva evento</Button>
                {eventForm.id && <Button variant="outline" onClick={() => setEventForm(emptyEvent)}>Annulla</Button>}
              </div>
            </div>

            
            <div className="bg-slate-900 border border-slate-800 rounded-md p-4">
              <h2 className="text-white font-semibold mb-3">Playlist M3U importate</h2>
              {playlists.length === 0 ? (
                <div className="text-sm text-slate-400">Nessuna playlist importata.</div>
              ) : (
                <div className="space-y-2">
                  {playlists.map(playlist => (
                    <div key={playlist.id} className="flex items-center justify-between gap-3 border border-slate-800 rounded-md p-3">
                      <div>
                        <div className="text-white font-medium">{playlist.name}</div>
                        <div className="text-xs text-slate-400">
                          {playlist.channels_count || 0} canali · aggiornata {playlist.updated_at || "-"}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-400"
                        onClick={() => deletePlaylist(playlist.id)}
                      >
                        <Trash2 size={14} className="mr-1" />
                        Elimina
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-md overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-950 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="text-left py-3 px-4">Evento</th>
                    <th className="text-left py-3 px-4">Canale</th>
                    <th className="text-left py-3 px-4">Stato</th>
                    <th className="text-left py-3 px-4 min-w-[640px]">Azioni evento</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map(event => {
                    const live = event.live || {};
                    const loadingAction = liveLoading[event.id];
                    const badgeClass = live.code === "live"
                      ? "bg-emerald-600/20 text-emerald-300"
                      : live.code === "starting"
                        ? "bg-amber-600/20 text-amber-300"
                        : live.code === "error"
                          ? "bg-red-600/20 text-red-300"
                          : "bg-slate-700/60 text-slate-300";

                    return (
                      <tr key={event.id} className="border-t border-slate-800">
                        <td className="py-3 px-4">
                          <div className="text-white font-medium">{event.title}</div>
                          <div className="text-xs text-slate-400">{event.competition} · {event.date} {event.time}</div>
                          {event.require_membership && (
                            <div className="text-[10px] text-emerald-300 mt-1">
                              Accesso protetto · {requiredChannels.filter(channel => channel.attivo && channel.obbligatorio).length} canali richiesti
                            </div>
                          )}
                          {live.failing_input_url && (
                            <div className="text-[10px] text-red-300 mt-1 truncate max-w-md">
                              URL input fallita: {live.failing_input_url}
                            </div>
                          )}
                        </td>
                        <td className="px-4 text-slate-300">{channelById[event.telegram_channel_id]?.name || "-"}</td>
                        <td className="px-4">
                          <span className={`text-[10px] uppercase px-2 py-1 rounded ${badgeClass}`}>
                            {live.label || event.status || "Offline"}
                          </span>
                          {live.pid && <div className="text-[10px] text-slate-500 mt-1">PID {live.pid}</div>}
                          {(live.last_error_label || live.last_error) && (
                            <div className="text-[10px] text-red-300 mt-1 max-w-52 truncate">
                              {live.last_error_label || "Errore"}{live.last_error ? `: ${live.last_error}` : ""}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            <Button size="sm" variant="outline" onClick={() => editEvent(event)} title="Modifica evento">
                              <Edit size={14} /> <span>Modifica</span>
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => deleteEvent(event.id)} className="text-red-300 border-red-900/60 hover:text-red-200" title="Elimina evento">
                              <Trash2 size={14} /> <span>Elimina</span>
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => publishEvent(event.id)} title="Invia su Telegram">
                              <Send size={14} /> <span>Invia Telegram</span>
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => copyEventLink(event)} title="Copia link evento">
                              <Copy size={14} /> <span>Copia link</span>
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => copyProtectedLink(event)} title="Copia link protetto">
                              <Copy size={14} /> <span>Link protetto</span>
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => testLiveSource(event)} disabled={!!loadingAction} title="Test sorgente">
                              <Radio size={14} /> <span>Test sorgente</span>
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => liveAction(event, "start")} disabled={!!loadingAction} title="Avvia Live">
                              <Play size={14} /> <span>Avvia Live</span>
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => liveAction(event, "stop")} disabled={!!loadingAction} title="Ferma Live">
                              <Square size={14} /> <span>Ferma Live</span>
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => liveAction(event, "restart")} disabled={!!loadingAction} title="Riavvia Live">
                              <RefreshCw size={14} /> <span>Riavvia Live</span>
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => openLiveLogs(event)} title="Log FFmpeg">
                              <ScrollText size={14} /> <span>Log FFmpeg</span>
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="sm" variant="outline" className="whitespace-nowrap">
                                  <MoreHorizontal size={14} /> Altre azioni
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-slate-900 border-slate-800 text-slate-100 min-w-56">
                                <DropdownMenuItem onClick={() => {
                                  const url = eventDeepLink(event);
                                  if (url) window.open(url, "_blank");
                                  else toast.error("Telegram Access Bot non configurato");
                                }} className="cursor-pointer gap-2">
                                  <ExternalLink size={14} /> Anteprima pagina accesso
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => duplicateEvent(event.id)} className="cursor-pointer gap-2">
                                  <Copy size={14} /> Duplica
                                </DropdownMenuItem>
                                <DropdownMenuSeparator className="bg-slate-800" />
                                <DropdownMenuItem onClick={() => openLiveLogs(event)} className="cursor-pointer gap-2">
                                  <ScrollText size={14} /> Visualizza log FFmpeg
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>

            {liveLogEvent && (
              <div className="bg-slate-900 border border-slate-800 rounded-md p-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <div className="text-white font-semibold">Log FFmpeg</div>
                    <div className="text-xs text-slate-500">{liveLogEvent.title}</div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openLiveLogs(liveLogEvent)}>Aggiorna</Button>
                    <Button size="sm" variant="ghost" onClick={() => setLiveLogEvent(null)}>Chiudi</Button>
                  </div>
                </div>
                <pre className="max-h-80 overflow-auto bg-slate-950 border border-slate-800 rounded-md p-3 text-xs text-slate-300 whitespace-pre-wrap">
                  {liveLogs.join("\n") || "Nessun log FFmpeg disponibile per questa live."}
                </pre>
              </div>
            )}
          </div>
        )}


        {tab === "library" && (
          <div className="space-y-6">
            <div className="bg-slate-900 border border-slate-800 rounded-md p-4 space-y-4">
              <h2 className="text-white font-semibold">Importa playlist M3U</h2>
              <div className="grid md:grid-cols-[280px_1fr] gap-4">
                <div>
                  <Label>Nome playlist</Label>
                  <Input value={playlistName} onChange={e => setPlaylistName(e.target.value)} className="bg-slate-950 border-slate-800 mt-1" />
                </div>
                <div>
                  <Label>File M3U</Label>
                  <Input type="file" accept=".m3u,.m3u8,.txt" onChange={e => loadPlaylistFile(e.target.files?.[0])} className="bg-slate-950 border-slate-800 mt-1" />
                </div>
              </div>
              <div>
                <Label>Contenuto playlist</Label>
                <textarea value={playlistContent} onChange={e => setPlaylistContent(e.target.value)} className="w-full min-h-[160px] bg-slate-950 border border-slate-800 rounded-md mt-1 px-3 py-2 text-sm text-slate-100 font-mono" />
              </div>
              <Button onClick={importPlaylist} className="bg-blue-600 hover:bg-blue-700">Importa / aggiorna playlist</Button>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-md p-4">
              <h2 className="text-white font-semibold mb-3">Playlist importate</h2>
              <div className="space-y-2">
                {playlists.map(pl => (
                  <div key={pl.id} className="flex items-center justify-between border border-slate-800 rounded p-3">
                    <div>
                      <div className="text-white">{pl.name}</div>
                      <div className="text-xs text-slate-400">{pl.channels_count || 0} canali</div>
                    </div>
                    <Button size="sm" variant="ghost" className="text-red-400" onClick={() => deletePlaylist(pl.id)}>Elimina</Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-md overflow-hidden">
              <div className="p-4 border-b border-slate-800">
                <div className="grid md:grid-cols-[1fr_260px_auto] gap-3 items-end">
                  <div>
                    <Label>Cerca canale</Label>
                    <Input value={librarySearch} onChange={e => changeLibrarySearch(e.target.value)} placeholder="RAI, Sky, DAZN, Eurosport..." className="bg-slate-950 border-slate-800 mt-1" />
                  </div>
                  <div>
                    <Label>Filtra gruppo</Label>
                    <Select value={libraryGroup || "__all"} onValueChange={changeLibraryGroup}>
                      <SelectTrigger className="bg-slate-950 border-slate-800 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 max-h-80">
                        <SelectItem value="__all">Tutti</SelectItem>
                        {libraryGroups.map(group => <SelectItem key={group} value={group}>{group}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={load} className="bg-blue-600 hover:bg-blue-700">Cerca</Button>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-950 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="text-left py-3 px-4">Canale</th>
                    <th className="text-left py-3 px-4">Gruppo</th>
                    <th className="text-left py-3 px-4">Stream</th>
                    <th className="text-right py-3 px-4">Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {libraryChannels
                    .filter(channel => !libraryGroup || channel.group === libraryGroup)
                    .filter(channel => !librarySearch || `${channel.name} ${channel.group} ${channel.tvg_id}`.toLowerCase().includes(librarySearch.toLowerCase()))
                    .map(channel => (
                    <tr key={channel.id} className="border-t border-slate-800">
                      <td className="py-3 px-4">
                        <Input value={channel.name || ""} onChange={e => updateLibraryChannel(channel.id, { name: e.target.value })} className="bg-slate-950 border-slate-800" />
                      </td>
                      <td className="px-4">
                        <Input value={channel.group || ""} onChange={e => updateLibraryChannel(channel.id, { group: e.target.value })} className="bg-slate-950 border-slate-800" />
                      </td>
                      <td className="px-4">
                        <div className="space-y-2">
                          <Input value={channel.stream_url || ""} onChange={e => updateLibraryChannel(channel.id, { stream_url: e.target.value })} className="bg-slate-950 border-slate-800" placeholder="Link principale" />
                          <Input value={channel.backup_1_url || ""} onChange={e => updateLibraryChannel(channel.id, { backup_1_url: e.target.value })} className="bg-slate-950 border-slate-800" placeholder="Backup 1" />
                          <Input value={channel.backup_2_url || ""} onChange={e => updateLibraryChannel(channel.id, { backup_2_url: e.target.value })} className="bg-slate-950 border-slate-800" placeholder="Backup 2" />
                        </div>
                      </td>
                      <td className="px-4 text-right">
                        <Button size="sm" variant={channel.active ? "default" : "outline"} onClick={() => updateLibraryChannel(channel.id, { active: !channel.active })}>
                          {channel.active ? "Attivo" : "Disattivo"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "access" && (
          <div className="space-y-6">
            <div className="grid gap-4 md:grid-cols-4">
              <div className="rounded-md border border-slate-800 bg-slate-900 p-4">
                <div className="text-xs uppercase text-slate-500">Canali attivi</div>
                <div className="mt-2 text-2xl font-bold text-white">{accessStats?.channels_count ?? requiredChannels.filter(c => c.attivo).length}</div>
              </div>
              <div className="rounded-md border border-slate-800 bg-slate-900 p-4">
                <div className="text-xs uppercase text-slate-500">Obbligatori</div>
                <div className="mt-2 text-2xl font-bold text-white">{accessStats?.mandatory_count ?? requiredChannels.filter(c => c.attivo && c.obbligatorio).length}</div>
              </div>
              <div className="rounded-md border border-slate-800 bg-slate-900 p-4">
                <div className="text-xs uppercase text-slate-500">Utenti verificati</div>
                <div className="mt-2 text-2xl font-bold text-white">{accessStats?.verified_users ?? 0}</div>
              </div>
                <div className="rounded-md border border-slate-800 bg-slate-900 p-4">
                <div className="text-xs uppercase text-slate-500">Verifiche fallite</div>
                <div className="mt-2 text-2xl font-bold text-white">{accessStats?.failed_checks ?? 0}</div>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="rounded-md border border-slate-800 bg-slate-900 p-4">
                <div className="text-xs uppercase text-slate-500">Iscritti oggi</div>
                <div className="mt-2 text-2xl font-bold text-white">{accessStats?.chart?.today ?? 0}</div>
              </div>
              <div className="rounded-md border border-slate-800 bg-slate-900 p-4">
                <div className="text-xs uppercase text-slate-500">Ultimi 7 giorni</div>
                <div className="mt-2 text-2xl font-bold text-white">{accessStats?.chart?.last_7_days ?? 0}</div>
              </div>
              <div className="rounded-md border border-slate-800 bg-slate-900 p-4">
                <div className="text-xs uppercase text-slate-500">Ultimi 30 giorni</div>
                <div className="mt-2 text-2xl font-bold text-white">{accessStats?.chart?.last_30_days ?? 0}</div>
              </div>
            </div>

            <div className="rounded-md border border-slate-800 bg-slate-900 p-4">
              <h2 className="mb-3 font-semibold text-white">Verifiche iscrizione</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={accessChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                    <XAxis dataKey="date" stroke="#94a3b8" tick={{ fontSize: 11 }} />
                    <YAxis stroke="#94a3b8" tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ background: "#0f172a", border: "1px solid #334155", color: "#e2e8f0" }}
                      labelStyle={{ color: "#e2e8f0" }}
                    />
                    <Bar dataKey="verified" name="Iscritti verificati" fill="#22c55e" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="checks" name="Verifiche" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid gap-6 xl:grid-cols-[420px_1fr]">
              <div className="rounded-md border border-slate-800 bg-slate-900 p-4 space-y-3">
                <h2 className="font-semibold text-white">{requiredChannelForm.id ? "Modifica canale obbligatorio" : "Nuovo canale obbligatorio"}</h2>
                <div>
                  <Label>Nome</Label>
                  <Input value={requiredChannelForm.nome || ""} onChange={e => setRequiredChannelForm({ ...requiredChannelForm, nome: e.target.value })} className="bg-slate-950 border-slate-800 mt-1" placeholder="CalcioNews24 Hub" />
                </div>
                <div>
                  <Label>Username Telegram</Label>
                  <Input value={requiredChannelForm.username || ""} onChange={e => setRequiredChannelForm({ ...requiredChannelForm, username: e.target.value })} className="bg-slate-950 border-slate-800 mt-1" placeholder="CalcioNews24Hub" />
                </div>
                <div>
                  <Label>Chat ID recuperato</Label>
                  <Input value={requiredChannelForm.telegram_chat_id || "Da verificare automaticamente"} readOnly className="bg-slate-950 border-slate-800 mt-1 text-slate-400" />
                  <div className="mt-1 text-xs text-slate-500">
                    Non devi inserirlo manualmente: premi Verifica canale.
                  </div>
                </div>
                <div>
                  <Label>Bot collegato</Label>
                  <Select value={requiredChannelForm.bot_channel_id || "__event"} onValueChange={value => setRequiredChannelForm({ ...requiredChannelForm, bot_channel_id: value === "__event" ? "" : value })}>
                    <SelectTrigger className="bg-slate-950 border-slate-800 mt-1"><SelectValue placeholder="Seleziona bot" /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                      <SelectItem value="__event">Usa bot dell'evento</SelectItem>
                      {channels.map(channel => <SelectItem key={channel.id} value={channel.id}>{channel.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="mt-1 text-xs text-slate-500">
                    Il bot scelto deve essere amministratore del canale obbligatorio.
                  </div>
                </div>
                <div>
                  <Label>Ordine</Label>
                  <Input type="number" value={requiredChannelForm.ordine ?? 0} onChange={e => setRequiredChannelForm({ ...requiredChannelForm, ordine: Number(e.target.value) })} className="bg-slate-950 border-slate-800 mt-1" />
                </div>
                <div className="flex items-center justify-between rounded-md border border-slate-800 bg-slate-950 p-3">
                  <div>
                    <Label>Obbligatorio</Label>
                    <div className="text-xs text-slate-500">Se attivo, l'utente deve risultare iscritto.</div>
                  </div>
                  <Switch checked={!!requiredChannelForm.obbligatorio} onCheckedChange={value => setRequiredChannelForm({ ...requiredChannelForm, obbligatorio: value })} />
                </div>
                <div className="flex items-center justify-between rounded-md border border-slate-800 bg-slate-950 p-3">
                  <div>
                    <Label>Attivo</Label>
                    <div className="text-xs text-slate-500">Mostra e verifica questo canale.</div>
                  </div>
                  <Switch checked={!!requiredChannelForm.attivo} onCheckedChange={value => setRequiredChannelForm({ ...requiredChannelForm, attivo: value })} />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={saveRequiredChannel} className="bg-blue-600 hover:bg-blue-700">
                    <Save size={16} className="mr-2" />
                    Salva canale
                  </Button>
                  <Button variant="outline" onClick={() => verifyRequiredChannel(requiredChannelForm)} disabled={!requiredChannelForm.id}>
                    <Radio size={16} className="mr-2" />
                    Verifica canale
                  </Button>
                  {requiredChannelForm.id && (
                    <Button variant="outline" onClick={() => setRequiredChannelForm(emptyRequiredChannel)}>
                      Annulla
                    </Button>
                  )}
                </div>
              </div>

              <div className="rounded-md border border-slate-800 bg-slate-900 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-950 text-xs uppercase text-slate-500">
                      <tr>
                        <th className="text-left py-3 px-4">Canale</th>
                        <th className="text-left py-3 px-4">Chat ID</th>
                        <th className="text-left py-3 px-4">Bot collegato</th>
                        <th className="text-left py-3 px-4">Stato bot</th>
                        <th className="text-left py-3 px-4">Ordine</th>
                        <th className="text-left py-3 px-4">Statistiche</th>
                        <th className="text-right py-3 px-4">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requiredChannels.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="py-8 px-4 text-center text-slate-400">
                            Nessun canale obbligatorio configurato.
                          </td>
                        </tr>
                      ) : requiredChannels.map(channel => (
                        <tr key={channel.id} className="border-t border-slate-800">
                          <td className="py-3 px-4">
                            <div className="font-medium text-white">{channel.nome || channel.username || "Canale Telegram"}</div>
                            {channel.username && <div className="text-xs text-slate-500">@{String(channel.username).replace(/^@/, "")}</div>}
                            {channel.config_message && <div className={channel.config_status === "configured" ? "mt-1 text-[10px] text-emerald-300" : "mt-1 text-[10px] text-red-300"}>{channel.config_message}</div>}
                          </td>
                          <td className="px-4 font-mono text-xs text-slate-400">{channel.telegram_chat_id || "Da verificare"}</td>
                          <td className="px-4 text-slate-300">{channel.bot_name || channelById[channel.bot_channel_id]?.name || "Bot evento"}</td>
                          <td className="px-4">
                            <div className="space-y-1">
                              <span className={`rounded px-2 py-1 text-[10px] uppercase ${
                                channel.bot_admin === true
                                  ? "bg-emerald-600/20 text-emerald-300"
                                  : channel.bot_admin === false
                                    ? "bg-red-600/20 text-red-300"
                                    : "bg-slate-700 text-slate-300"
                              }`}>
                                {channel.bot_status || "Da evento"}
                              </span>
                              {channel.bot_error && <div className="max-w-44 truncate text-[10px] text-red-300">{channel.bot_error}</div>}
                            </div>
                          </td>
                          <td className="px-4 text-slate-300">{channel.ordine ?? 0}</td>
                          <td className="px-4">
                            <div className="flex flex-wrap gap-1 mb-2">
                              <span className={`rounded px-2 py-1 text-[10px] uppercase ${channel.attivo ? "bg-emerald-600/20 text-emerald-300" : "bg-slate-700 text-slate-300"}`}>
                                {channel.attivo ? "Attivo" : "Disattivo"}
                              </span>
                              {channel.obbligatorio && <span className="rounded bg-blue-600/20 px-2 py-1 text-[10px] uppercase text-blue-300">Obbligatorio</span>}
                            </div>
                            <div className="grid min-w-64 grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-slate-400">
                              <span>Iscritti verificati: <b className="text-slate-100">{channel.stats?.verified_subscribers ?? 0}</b></span>
                              <span>Accessi: <b className="text-slate-100">{channel.stats?.accesses ?? 0}</b></span>
                              <span>Verifiche: <b className="text-slate-100">{channel.stats?.verifications ?? 0}</b></span>
                              <span>Fallite: <b className="text-slate-100">{channel.stats?.failed ?? 0}</b></span>
                              <span>Successo: <b className="text-slate-100">{channel.stats?.success_rate ?? 0}%</b></span>
                              <span>Ultima verifica: <b className="text-slate-100">{channel.stats?.last_verification_at ? String(channel.stats.last_verification_at).slice(0, 16) : "-"}</b></span>
                              <span className="col-span-2">Ultimo accesso: <b className="text-slate-100">{channel.stats?.last_access_at ? String(channel.stats.last_access_at).slice(0, 16) : "-"}</b></span>
                            </div>
                          </td>
                          <td className="px-4 text-right">
                            <div className="flex justify-end gap-2">
                              <Button size="sm" variant="outline" onClick={() => setRequiredChannelForm({ ...emptyRequiredChannel, ...channel })}>
                                Modifica
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => verifyRequiredChannel(channel)}>
                                Verifica canale
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-400" onClick={() => deleteRequiredChannel(channel.id)}>
                                Elimina
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="rounded-md border border-slate-800 bg-slate-900 p-4">
              <h2 className="mb-3 font-semibold text-white">Ultime verifiche</h2>
              <div className="space-y-2">
                {(accessStats?.recent || []).length === 0 ? (
                  <div className="text-sm text-slate-400">Nessuna verifica registrata.</div>
                ) : accessStats.recent.map(row => (
                  <div key={row.id} className="flex flex-wrap items-center justify-between gap-3 rounded border border-slate-800 p-3 text-sm">
                    <div>
                      <div className="text-white">{row.username || row.telegram_user_id || "Utente Telegram"}</div>
                      <div className="text-xs text-slate-500">{row.created_at}</div>
                    </div>
                    <span className={row.ok ? "text-emerald-300" : "text-red-300"}>
                      {row.ok ? "Accesso verificato" : "Iscrizione mancante"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "channels" && (
          <div className="grid lg:grid-cols-[420px_1fr] gap-6">
            <div className="bg-slate-900 border border-slate-800 rounded-md p-4 space-y-3">
              <h2 className="text-white font-semibold">{channelForm.id ? "Modifica canale" : "Nuovo canale Streaming"}</h2>
              <div><Label>Nome</Label><Input value={channelForm.name || ""} onChange={e => setChannelForm({ ...channelForm, name: e.target.value })} className="bg-slate-950 border-slate-800 mt-1" /></div>
              <div><Label>Bot Token</Label><Input type="password" value={channelForm.bot_token || ""} onChange={e => setChannelForm({ ...channelForm, bot_token: e.target.value })} className="bg-slate-950 border-slate-800 mt-1" /></div>
              <div><Label>Chat ID</Label><Input value={channelForm.chat_id || ""} onChange={e => setChannelForm({ ...channelForm, chat_id: e.target.value })} className="bg-slate-950 border-slate-800 mt-1" /></div>
              <Button onClick={saveChannel} className="bg-blue-600 hover:bg-blue-700"><Plus size={16} className="mr-2" />Salva canale</Button>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <tbody>
                  {channels.map(channel => (
                    <tr key={channel.id} className="border-b border-slate-800">
                      <td className="py-3 px-4 text-white">{channel.name}</td>
                      <td className="px-4 text-slate-400 font-mono text-xs">{channel.chat_id}</td>
                      <td className="px-4 text-right">
                        <Button size="sm" variant="ghost" onClick={() => setChannelForm(channel)}>Modifica</Button>
                        <Button size="sm" variant="ghost" className="text-red-400" onClick={() => deleteChannel(channel.id)}><Trash2 size={14} /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "templates" && (
          <div className="bg-slate-900 border border-slate-800 rounded-md p-4 space-y-4">
            <div><Label>Nome template</Label><Input value={templateForm.name || ""} onChange={e => setTemplateForm({ ...templateForm, name: e.target.value })} className="bg-slate-950 border-slate-800 mt-1" /></div>
            <div>
              <Label>Testo Telegram</Label>
              <textarea
                value={templateForm.body || ""}
                onChange={e => setTemplateForm({ ...templateForm, body: e.target.value })}
                className="w-full min-h-[220px] bg-slate-950 border border-slate-800 rounded-md mt-1 px-3 py-2 text-sm text-slate-100 font-mono"
              />
            </div>
            <div>
              <Label>Pulsanti, uno per riga: testo|url</Label>
              <textarea
                value={buttonsText}
                onChange={e => setButtonsText(e.target.value)}
                className="w-full min-h-[90px] bg-slate-950 border border-slate-800 rounded-md mt-1 px-3 py-2 text-sm text-slate-100 font-mono"
              />
            </div>
            <div className="text-xs text-slate-400">
              Variabili: {"{title} {description} {competition} {sport} {home_team} {away_team} {date} {time} {status} {watch_url} {telegram_deep_link}"}
            </div>
            <Button onClick={saveTemplate} className="bg-blue-600 hover:bg-blue-700"><Save size={16} className="mr-2" />Salva template</Button>
          </div>
        )}
      </PageContent>
    </div>
  );
}
